import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { authService, type OAuthProvider } from '@/services/authService';

export function OAuthCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { provider } = useParams<{ provider: string }>();
  const { login } = useAuthStore();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      // Check for direct token (legacy flow)
      const token = searchParams.get('token');
      const code = searchParams.get('code');
      const errorParam = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      if (errorParam) {
        setError(errorDescription || errorParam);
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      // Legacy flow: token directly in URL
      if (token) {
        try {
          localStorage.setItem('temp-token', token);
          const user = await authService.getCurrentUser();
          login(user, token);
          localStorage.removeItem('temp-token');
          navigate('/dashboard');
        } catch {
          setError('Failed to complete authentication');
          setTimeout(() => navigate('/login'), 3000);
        }
        return;
      }

      // New flow: exchange code for token
      if (code && provider && ['google', 'github'].includes(provider)) {
        try {
          const response = await authService.handleOAuthCallback(
            provider as OAuthProvider,
            { code }
          );

          login(response.user, response.token);
          navigate('/dashboard');
        } catch (err) {
          console.error('OAuth callback error:', err);
          setError('Failed to complete authentication');
          setTimeout(() => navigate('/login'), 3000);
        }
        return;
      }

      setError('No authentication token or code received');
      setTimeout(() => navigate('/login'), 3000);
    };

    handleCallback();
  }, [searchParams, navigate, login, provider]);

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        {error ? (
          <>
            <div className="rounded-full bg-destructive/10 p-4 inline-block">
              <svg
                className="h-8 w-8 text-destructive"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h2 className="mt-4 text-lg font-semibold">Authentication Failed</h2>
            <p className="mt-2 text-sm text-muted-foreground">{error}</p>
            <p className="mt-2 text-xs text-muted-foreground">
              Redirecting to login...
            </p>
          </>
        ) : (
          <>
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            <h2 className="mt-4 text-lg font-semibold">Completing sign in...</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Please wait while we authenticate you.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
